import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import useFloatingPanel from './useFloatingPanel';
import '../../assets/styles/SelectMenu.css';

// inline: la lista se abre debajo del boton en vez de flotar en <body>. Hace
// falta dentro de un modal, que tiene transform y atrapa el foco.
const SelectMenu = ({ value, options = [], onChange, label, placeholder, icon, id, disabled = false, inline = false, className = '' }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const focusedRef = useRef(false);
  const listId = useId();
  const { style, placement, ready } = useFloatingPanel(open, triggerRef, listRef, { align: 'end', matchWidth: true });

  const selected = options.find((option) => option.value === value);
  const shownLabel = selected && selected.value !== '' ? selected.label : placeholder;

  const optionNodes = () => [...(listRef.current?.querySelectorAll('.tp-dropdown__option') || [])];

  useEffect(() => {
    if (!open) {
      focusedRef.current = false;
      return;
    }
    if (ready && !focusedRef.current) {
      focusedRef.current = true;
      const nodes = optionNodes();
      const current = nodes.find((node) => node.getAttribute('aria-selected') === 'true') || nodes[0];
      if (current) current.focus();
    }
  }, [open, ready]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      const inside = rootRef.current?.contains(event.target) || listRef.current?.contains(event.target);
      if (!inside) setOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const choose = (option) => {
    setOpen(false);
    triggerRef.current?.focus();
    if (option.value !== value) onChange(option.value);
  };

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const handleTriggerKey = (event) => {
    if (disabled) return;
    if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
    }
  };

  const handleListKey = (event) => {
    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nodes = optionNodes();
    const index = nodes.indexOf(document.activeElement);
    const last = nodes.length - 1;
    const next = {
      ArrowDown: index === last ? 0 : index + 1,
      ArrowUp: index <= 0 ? last : index - 1,
      Home: 0,
      End: last
    }[event.key];
    nodes[next].focus();
  };

  const list = (
    <div
      ref={listRef}
      id={listId}
      className={`tp-dropdown__list ${inline ? 'is-inline' : ''} ${!inline && placement ? `is-${placement}` : ''}`.replace(/\s+/g, ' ').trim()}
      style={inline ? undefined : style}
      role="listbox"
      aria-label={label}
      onKeyDown={handleListKey}
    >
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value || 'default'}
            type="button"
            role="option"
            aria-selected={isSelected}
            className={`tp-dropdown__option ${isSelected ? 'is-selected' : ''}`.trim()}
            onClick={() => choose(option)}
          >
            <span>{option.label}</span>
            {isSelected && <Check size={16} strokeWidth={2.25} aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={`tp-dropdown ${disabled ? 'is-disabled' : ''} ${className}`.replace(/\s+/g, ' ').trim()} ref={rootRef}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        className={`tp-dropdown__trigger ${open ? 'is-open' : ''} ${selected && selected.value !== '' ? 'has-value' : ''}`.replace(/\s+/g, ' ').trim()}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={label ? `${label}: ${shownLabel}` : undefined}
        onClick={() => { if (!disabled) setOpen((current) => !current); }}
        onKeyDown={handleTriggerKey}
      >
        {icon && <span className="tp-dropdown__icon" aria-hidden="true">{icon}</span>}
        <span className="tp-dropdown__value">{shownLabel}</span>
        <ChevronDown className="tp-dropdown__chevron" size={16} strokeWidth={2} aria-hidden="true" />
      </button>

      {open && (inline ? list : createPortal(list, document.body))}
    </div>
  );
};

export default SelectMenu;
export { SelectMenu };
